import { getReporter } from "./bootstrap"

export interface ReportOptions {
  alertUser?: boolean
}

abstract class TryCatchResult<T> {
  /** Discriminant to narrow on: `true` for {@link Ok}, `false` for {@link Err}. */
  abstract readonly ok: boolean

  /**
   * Returns the success value, rethrowing the error if there isn't one.
   *
   * @returns The `Ok` value.
   * @throws The `Err` error, unchanged.
   */
  abstract unwrap(): T

  /**
   * Runs a side effect on the success value and passes the result through,
   * so it can be chained. Skipped entirely on `Err`.
   *
   * @param cb Invoked with the value only when this is an `Ok`.
   * @returns This same result.
   */
  abstract tap(cb: (value: T) => void): Ok<T> | Err

  /**
   * Reports the error via {@link reportError} when there is one; no-op on `Ok`.
   * Use this where the same code path handles both outcomes.
   *
   * @param options Pass `{ alertUser: false }` to report without toasting.
   */
  abstract reportIfError(options?: ReportOptions): void
}

export class Ok<T> extends TryCatchResult<T> {
  readonly ok = true

  constructor(readonly value: T) {
    super()
  }

  override unwrap() {
    return this.value
  }

  override tap(cb: (value: T) => void) {
    cb(this.value)
    return this
  }

  override reportIfError() {
    return /* no-op */
  }
}

export class Err<T = unknown> extends TryCatchResult<never> {
  readonly ok = false

  constructor(readonly error: T) {
    super()
  }

  override unwrap(): never {
    throw this.error
  }

  override tap(_cb: (value: never) => void) {
    return this
  }

  override reportIfError(options?: ReportOptions) {
    this.report(options)
  }

  /**
   * Same as {@link TryCatchResult.reportIfError}, but only callable once
   * narrowed to `Err` — and it returns itself so it can be chained.
   *
   * @param options Pass `{ alertUser: false }` to report without toasting.
   * @returns This same result.
   */
  report({ alertUser = true }: ReportOptions = {}) {
    getReporter().reportError(this.error, { alertUser })
    return this
  }
}
